import { v4 as uuidv4 } from 'uuid';
import { log } from '@/logger/logger.ts';
import { HandleType } from '@/enums/handle-type.enum';
import { ModelType } from '@/enums/model-type.enum';
import { ModelAssetToUploadedAsset, UploadedAssetToModelAsset, type UploadedAsset } from '@/types/api/assets';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { isMediaAsset } from '@/components/Common/ImageList/utils';
import { UndoRedoEntry } from '../undoRedo';
import { handleTypeToDataType, type FlowGraph } from '../FlowGraph';
import { ImportModelView } from './ImportModelView';
import type { DownloadableAsset } from '@/types/downloadable';
import type { NodeId } from 'web';
import type { AiModelData, ModelBaseNodeData } from '@/types/nodes/model';
import type { ClearableAssetsView } from './NodeAssetManager';

const logger = log.getLogger('ModelBaseView');

export interface DynamicInputConfig {
  prefix: string;
  handleType: HandleType;
}

export const mergeSeedParam = (params: ModelBaseNodeData['params'], result?: ModelBaseNodeData['result'][number]) => {
  if (!result || !params || !params.seed) {
    return params;
  }
  if (!('input' in result && 'seed' in result.input)) {
    return params;
  }
  const resultSeed = result.input.seed;
  if (typeof resultSeed !== 'number') {
    return params;
  }
  const seedParam = params.seed as { isRandom: boolean; seed: number };
  return {
    ...params,
    seed: { ...seedParam, seed: resultSeed },
  };
};

export class ModelBaseView implements ClearableAssetsView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  setSelectedIndex(newSelectedOutput: number) {
    this.graph.edit(() => this.updateIndex(newSelectedOutput));
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadImageResources();
  }

  appendResult(resultsToAppend: UploadedAsset[], runIndexInBatch: number, batchId: string, secondaryOrder: number) {
    if (resultsToAppend.length === 0) {
      return;
    }
    const currentResult = this.getGenerations();
    if (hasExistingGenerationForIncomingRunId(currentResult, runIndexInBatch, batchId, secondaryOrder)) {
      logger.error('appendResult skipped: generation already exists for this batch run slot', {
        nodeId: this.nodeId,
        batchId,
        runIndexInBatch,
        secondaryOrder,
      });
      return;
    }
    const index = findInsertionIndex(currentResult, runIndexInBatch, batchId, secondaryOrder);
    const newResultArray = [...currentResult.slice(0, index), ...resultsToAppend, ...currentResult.slice(index)];
    const newSelectedIndex = index + resultsToAppend.length - 1;

    this.updateGenerations(newResultArray);
    this.updateIndex(newSelectedIndex);
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }

  deleteResult(index?: number) {
    const generations = this.getGenerations();
    const selectedOutput = index ?? this.getSelectedIndex();
    const newResult = generations.filter((_, index) => index !== selectedOutput);
    this.graph.edit(() => this.updateGenerations(newResult));
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }

  deleteAllOtherResults(index?: number) {
    const generations = this.getGenerations();
    const selectedOutput = index ?? this.getSelectedIndex();
    const newResult = generations.filter((_, index) => index === selectedOutput);
    this.graph.edit(() => this.updateGenerations(newResult));
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }

  deleteAllResults() {
    this.graph.edit(() => this.updateGenerations([]));
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }

  /**
   * Clears all assets from this view (returns UndoRedoEntry for batching with other operations)
   */
  clearAllAssets(): UndoRedoEntry {
    return this.updateGenerations([]);
  }

  /**
   * Checks if this view has any assets
   */
  hasAssets(): boolean {
    return this.getGenerations().length > 0;
  }

  /**
   * Clears all assets except the currently selected one (returns UndoRedoEntry for batching)
   * Uses skipPostProcessing so the batch caller (e.g. NodeAssetManager.clearOtherAssetsFromNodes) can run loadResources/updateNodeOutputs once.
   */
  clearOtherAssets(): UndoRedoEntry {
    const generations = this.getGenerations();
    const selectedOutput = this.getSelectedIndex();
    const newResult = generations.filter((_, index) => index === selectedOutput);
    return this.updateGenerations(newResult);
  }

  hasMultipleAssets(): boolean {
    return this.getGenerations().length > 1;
  }

  editText(index: number, newValue: string) {
    this.graph.wasmChange((wasm) => wasm.editTextAsset(this.nodeId, newValue));
    this.graph.updateNodeOutputs(this.nodeId, false);
  }

  private updateIndex(newSelectedOutput: number): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    const { params } = this.getData();
    undoEntry.add(this.graph.wasmChange((wasm) => wasm.setSelectedMedia(this.nodeId, newSelectedOutput)));
    const newParams = mergeSeedParam(params, this.getGenerations()[newSelectedOutput]);
    if (newParams !== params) {
      undoEntry.add(
        this.graph.updateModelNodeData(this.nodeId, {
          params: newParams,
        }),
      );
    }
    return undoEntry;
  }

  private updateGenerations(newGenerations: UploadedAsset[]): UndoRedoEntry {
    const currentIndex = this.getSelectedIndex();
    const undoEntry = UndoRedoEntry.empty();
    const { params: currentParams } = this.getData();
    undoEntry.add(
      this.graph.wasmChange((wasm) =>
        wasm.setModelGenerations(
          this.nodeId,
          newGenerations.map((asset) => {
            const modelAsset = UploadedAssetToModelAsset(asset);
            if (modelAsset === undefined) {
              throw new Error(`Invalid media asset: ${asset.type}`);
            }
            return modelAsset;
          }),
        ),
      ),
    );
    if (newGenerations.length - 1 < currentIndex) {
      undoEntry.add(this.graph.wasmChange((wasm) => wasm.setSelectedMedia(this.nodeId, 0)));
      const newParams = mergeSeedParam(currentParams, newGenerations[0]);
      if (newParams !== currentParams) {
        undoEntry.add(this.graph.updateModelNodeData(this.nodeId, { params: newParams }));
      }
    }
    return undoEntry;
  }

  private addInputHandleInternal({
    type,
    key,
    order,
    format,
    label,
  }: {
    type: HandleType;
    key: string;
    order: number;
    format: string;
    label?: string;
  }): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    const validType = handleTypeToDataType(type);
    const validInputTypes = validType ? [validType] : undefined;
    undoEntry.add(this.graph.wasmChange((wasm) => wasm.addInputHandle(this.nodeId, key, validInputTypes)));

    undoEntry.add(
      this.graph.addNodeInputHandle(this.nodeId, key, {
        description: '',
        format,
        id: uuidv4(),
        label: label ?? key,
        order,
        required: false,
        type,
      }),
    );
    return undoEntry;
  }

  /**
   * Determines the dynamic input config (prefix + handle type) for a model.
   * Returns null for models that don't support standard dynamic inputs
   * (e.g. FalFluxMultiLora uses lora handles instead).
   */
  getDynamicInputConfig(modelName: string): DynamicInputConfig | null {
    const isFalFluxMultiLora =
      modelName === ModelType.FalFluxMultiLora.valueOf() ||
      modelName === ModelType.FalFluxMultiLoraInpaint.valueOf() ||
      modelName === ModelType.FalFluxKontextLora.valueOf();
    if (isFalFluxMultiLora) return null;

    const klingType = ModelType.Kling.valueOf();
    const data = this.graph.store.getNodeDataNullable<AiModelData>(this.nodeId);
    const isKling3Model =
      (modelName === klingType && data?.kind?.type === klingType) ||
      modelName === 'fal-ai/kling-video/v2.6/standard/motion-control' ||
      modelName === 'fal-ai/kling-video/v2.6/pro/motion-control';
    if (isKling3Model) return { prefix: 'element', handleType: HandleType.KlingElement };

    if (modelName === 'bytedance/seedance-2.0/reference-to-video') {
      return { prefix: 'reference', handleType: HandleType.Any };
    }

    if (modelName === 'openrouter/router/video/enterprise') {
      return { prefix: 'video', handleType: HandleType.Video };
    }

    const importModelView = new ImportModelView(this.graph, this.nodeId);
    const dynamicHandleName = importModelView.getDynamicHandleName(modelName);
    if (!dynamicHandleName) return null;

    if (modelName === 'lucataco/video-merge') {
      return { prefix: dynamicHandleName, handleType: HandleType.Video };
    }

    return { prefix: dynamicHandleName, handleType: HandleType.Image };
  }

  addInputHandle(prefix: string, type: HandleType) {
    this.graph.edit(() => {
      return this.addInputHandleWithKey(prefix, type).undoEntry;
    });
  }

  /**
   * Adds a dynamic input handle and returns the new handle key along with the undo entry.
   * Does NOT wrap in graph.edit() — the caller is responsible for that.
   */
  addInputHandleWithKey(prefix: string, type: HandleType): { key: string; undoEntry: UndoRedoEntry } {
    const { handles } = this.graph.store.getNodeData<ModelBaseNodeData>(this.nodeId);
    const numInputsWithPrefix = Object.keys(handles.input).filter((key) => key.includes(prefix)).length;
    const newKey = `${prefix}_${numInputsWithPrefix + 1}`;
    const order = Object.keys(handles.input).length;
    const undoEntry = this.addInputHandleInternal({ type, key: newKey, order, format: 'uri' });
    return { key: newKey, undoEntry };
  }

  addLoraInputHandles() {
    this.graph.edit(() => {
      const { handles } = this.graph.store.getNodeData<ModelBaseNodeData>(this.nodeId);
      const numberOfLoraInputs = Object.keys(handles.input).filter((key) => key.includes('lora')).length / 2;
      const loraKey = `lora_${numberOfLoraInputs + 1}`;
      const weightKey = `lora_${numberOfLoraInputs + 1}_scale`;
      const order = Object.keys(handles.input).length;

      const undoEntry = UndoRedoEntry.empty();
      undoEntry.add(
        this.addInputHandleInternal({
          key: loraKey,
          format: 'uri',
          label: `loRA_${numberOfLoraInputs + 1}`,
          order,
          type: HandleType.Lora,
        }),
      );
      undoEntry.add(
        this.addInputHandleInternal({
          key: weightKey,
          format: 'number',
          label: `loRA_${numberOfLoraInputs + 1}_weight`,
          order: order + 1,
          type: HandleType.Number,
        }),
      );
      return undoEntry;
    });
  }

  private getData(): ModelBaseNodeData {
    return this.graph.store.getNodeData<ModelBaseNodeData>(this.nodeId);
  }

  private getGenerations(): UploadedAsset[] {
    const data = this.graph.store.getNodeData<AiModelData>(this.nodeId);
    return data.generations?.map(ModelAssetToUploadedAsset) ?? [];
  }

  private getSelectedIndex(): number {
    const data = this.graph.store.getNodeData<AiModelData>(this.nodeId);
    return data.selectedIndex;
  }

  setViewMode(viewMode: NodeViewMode) {
    this.graph.store.updateNodeData<ModelBaseNodeData>(this.nodeId, (_) => {
      return {
        viewMode,
      };
    });
  }

  getAllDownloadableAssets(): DownloadableAsset[] {
    return this.getGenerations().filter(isMediaAsset);
  }

  /**
   * Gets a downloadable asset at a specific index
   */
  getDownloadableAssetAtIndex(index: number): DownloadableAsset | null {
    const generations = this.getGenerations();
    const asset = generations[index];

    // Check if the asset is downloadable (not text or rendering, and has a url)
    if (!asset || !isMediaAsset(asset) || !asset.url) {
      return null;
    }

    return asset as DownloadableAsset;
  }

  /**
   * Gets the currently selected downloadable asset
   */
  getCurrentDownloadableAsset(): DownloadableAsset | null {
    const selectedIndex = this.getSelectedIndex();
    return this.getDownloadableAssetAtIndex(selectedIndex);
  }
}

/** True if a generation already exists for this batch run slot (avoids duplicate appends from out-of-order / replayed updates). */
function hasExistingGenerationForIncomingRunId(
  generations: UploadedAsset[],
  runIndexInBatch: number,
  batchId: string,
  secondaryOrder: number,
): boolean {
  return generations.some(
    (g) => g.batchId === batchId && g.order === runIndexInBatch && g.secondaryOrder === secondaryOrder,
  );
}

function compareSecondaryOrder(existingSecondary: number | undefined, newSecondary: number | undefined): boolean {
  // Both undefined: maintain current behavior (insert after)
  if (existingSecondary === undefined && newSecondary === undefined) {
    return true;
  }
  // Only existing is undefined: new item (with secondary order) comes first
  if (existingSecondary === undefined) {
    return false;
  }
  // Only new is undefined: insert after existing item
  if (newSecondary === undefined) {
    return true;
  }
  // Both defined: normal ascending comparison
  return existingSecondary <= newSecondary;
}

function findInsertionIndex(
  array: ModelBaseNodeData['result'],
  order: number,
  batchId: string,
  secondaryOrder?: number,
): number {
  if (!array?.length) {
    return 0;
  }

  // Find all items in the same batch (they will all have order too)
  type BatchItem = {
    item: ModelBaseNodeData['result'][number];
    index: number;
    order: number;
    secondaryOrder?: number;
  };
  const sameBatchItems: BatchItem[] = [];

  for (let i = 0; i < array.length; i++) {
    const currentItem = array[i];

    // Since batch items always have order, we can check batchId directly
    if (currentItem.batchId === batchId) {
      const order = currentItem.order as number;
      const secondaryOrder = currentItem.secondaryOrder as number | undefined;
      sameBatchItems.push({
        item: currentItem,
        index: i,
        order,
        secondaryOrder,
      });
    }
  }

  // If no items in the same batch exist, insert at the end
  if (sameBatchItems.length === 0) {
    return array.length;
  }

  // Binary search within the same batch's items
  let left = 0;
  let right = sameBatchItems.length;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    const midItem = sameBatchItems[mid];

    const shouldInsertAfter =
      midItem.order < order ||
      (midItem.order === order && compareSecondaryOrder(midItem.secondaryOrder, secondaryOrder));

    if (shouldInsertAfter) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  // Determine the actual array index for insertion within the batch
  if (left === 0) {
    // Insert before the first item in the batch
    return sameBatchItems[0].index;
  } else if (left === sameBatchItems.length) {
    // Insert after the last item in the batch
    return sameBatchItems[sameBatchItems.length - 1].index + 1;
  } else {
    // Insert between two items in the same batch
    return sameBatchItems[left].index;
  }
}
